import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import SubscriptionStatus from '../components/SubscriptionStatus';

// Pitch-deck v2 + research-doc pricing model (see server/config/plans.js).
// Per-event tiers shown inline as a sub-table inside the Per-Event card —
// each is the same plan id (`per_event`), the band is picked at billing time
// from the event's expected guest count.
const PER_EVENT_BUNDLES = [
	{ label: 'Essential', range: '≤200', kit: 'Software only · BYO', price: '₦15,000' },
	{ label: 'Essential XL', range: '201–400', kit: 'Software only · BYO', price: '₦25,000' },
	{ label: 'Essential XXL', range: '401+', kit: 'Software only · BYO', price: '₦35,000' },
	{ label: 'Standard', range: '201–400', kit: 'Software + 7 tablets · Ops + 1 runner', price: '₦95,000', highlight: true },
	{ label: 'Premium', range: '401+', kit: 'Software + 10 tablets · Ops + 2 runners + setup', price: '₦135,000' },
];

const plans = [
	{
		id: 'free',
		name: 'Free',
		price: '₦0',
		priceSub: 'first 2 events · software only',
		description: 'Your first 2 events use Eventory\'s software free of charge. Bring your own tablets and team. Need tablets or Ops on a specific free event? Attach a service pack à la carte (₦55-120K by size).',
	},
	{
		id: 'per_event',
		name: 'Per-Event',
		price: '₦15K · ₦95K · ₦135K',
		priceSub: 'by guest count',
		description: 'Essential is software-only (you bring tablets and team). Standard and Premium are full-service — software + tablets + Ops Manager (+ runners). Pick the tier that matches each event\'s size.',
		bundles: PER_EVENT_BUNDLES,
	},
	{
		id: 'oga',
		name: 'Oga (Self-serve)',
		price: '₦75,000',
		priceSub: 'per month · software only',
		description: 'Unlimited events, all premium software features. No Ops Manager or pre-loaded tablets — you bring your own. Worth it at 5+ events/month (below that, pay per event on Essential).',
	},
];

const PricingPage = () => {
	const [dashboard, setDashboard] = useState(null);
	const [subscription, setSubscription] = useState(null);
	const [loading, setLoading] = useState(true);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');
	const [submittingPlanId, setSubmittingPlanId] = useState('');

	const loadState = async () => {
		setLoading(true);
		try {
			const [dashboardRes, subscriptionRes] = await Promise.all([
				api.get('/account/dashboard'),
				api.get('/payments/subscription'),
			]);
			setDashboard(dashboardRes.data);
			setSubscription(subscriptionRes.data?.subscription || null);
		} catch (err) {
			setError(err.response?.data?.msg || 'Failed to load pricing details.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadState();
	}, []);

	const currentPlanId = useMemo(() => dashboard?.plan?.id || 'free', [dashboard]);
	// 'baller' is the deprecated legacy alias — treat it as 'oga' for the
	// "current plan" badge so legacy subscribers don't see a phantom tier.
	const normalizedCurrentPlanId = currentPlanId === 'baller' ? 'oga' : currentPlanId;

	const handleSubscribe = async (planId) => {
		setSubmittingPlanId(planId);
		setError('');
		setMessage('');
		try {
			const res = await api.post('/payments/subscribe', { planId });
			if (res.data?.manualRenewal && res.data?.authorization_url) {
				window.open(res.data.authorization_url, '_blank', 'noopener,noreferrer');
				setMessage('Manual renewal checkout opened in a new tab.');
			} else {
				setMessage('Subscription updated successfully.');
			}
			await loadState();
		} catch (err) {
			setError(err.response?.data?.msg || 'Failed to start subscription.');
		} finally {
			setSubmittingPlanId('');
		}
	};

	const handleCancel = async () => {
		setSubmittingPlanId('cancel');
		setError('');
		setMessage('');
		try {
			await api.post('/payments/cancel-subscription');
			setMessage('Subscription will stop auto-renewing at the end of the current period.');
			await loadState();
		} catch (err) {
			setError(err.response?.data?.msg || 'Failed to cancel subscription.');
		} finally {
			setSubmittingPlanId('');
		}
	};

	return (
		<div className="ev-user-shell">
			<header className="ev-admin-topbar">
				<div className="ev-admin-topbar-inner">
					<div className="ev-admin-topbar-left">
						<div className="ev-brand-lockup">
							<img src="/images/eventory-logo.svg" alt="Eventory" />
						</div>
					</div>
				</div>
			</header>
			<main className="ev-user-dash">
				<div className="ev-user-wrap">
					<div className="ev-admin-header">
						<h2 className="ev-section-title">Pricing & Plans</h2>
					</div>
					{message ? <div className="ev-alert ev-alert-success">{message}</div> : null}
					{error ? <div className="ev-alert ev-alert-danger">{error}</div> : null}
					{!loading ? (
						<SubscriptionStatus
							subscription={subscription}
							onCancel={subscription ? handleCancel : null}
							cancelling={submittingPlanId === 'cancel'}
						/>
					) : null}
					<div className="ev-event-grid">
						{plans.map((plan) => {
							const isCurrent =
								normalizedCurrentPlanId === plan.id || subscription?.planId === plan.id ||
								(plan.id === 'oga' && subscription?.planId === 'baller');
							const isPaidTier = plan.id === 'oga';
							return (
								<div
									key={plan.id}
									className="ev-event-card"
									style={plan.highlight ? { border: '2px solid var(--ev-brand, #3D1E6D)' } : undefined}
								>
									<div className="ev-admin-header ev-tight">
										<h3 className="ev-zero-margin">{plan.name}</h3>
										{isCurrent ? <span className="ev-chip is-primary">Current</span> : null}
										{plan.highlight && !isCurrent ? <span className="ev-chip is-soft">Most popular</span> : null}
									</div>
									<div className="ev-top-gap" style={{ fontSize: '28px', fontWeight: 800 }}>{plan.price}</div>
									{plan.priceSub ? <div className="ev-subtle">{plan.priceSub}</div> : null}
									<p className="ev-event-copy">{plan.description}</p>

									{plan.bundles ? (
										<div className="ev-stack" style={{ gap: '6px', marginTop: '8px' }}>
											{plan.bundles.map((b) => (
												<div
													key={b.label}
													style={{
														display: 'flex',
														justifyContent: 'space-between',
														padding: '8px 10px',
														borderRadius: '8px',
														background: b.highlight ? 'rgba(251, 134, 1, 0.08)' : 'rgba(17,17,19,0.03)',
														fontSize: '13px',
													}}
												>
													<div>
														<strong>{b.label}</strong>
														<div className="ev-subtle" style={{ fontSize: '12px' }}>{b.range} · {b.kit}</div>
													</div>
													<strong>{b.price}</strong>
												</div>
											))}
										</div>
									) : null}

									{isPaidTier ? (
										<button
											type="button"
											className="ev-primary-btn ev-top-gap"
											onClick={() => handleSubscribe(plan.id)}
											disabled={submittingPlanId === plan.id}
										>
											{submittingPlanId === plan.id ? 'Processing…' : `Choose ${plan.name}`}
										</button>
									) : (
										<div className="ev-subtle ev-top-gap">
											{plan.id === 'free' ? 'Automatic for new accounts' : 'Charged per event when you activate it'}
										</div>
									)}
								</div>
							);
						})}
					</div>

					{subscription?.planId === 'baller' && (
						<div className="ev-alert ev-top-gap">
							You're on a legacy Baller plan. It keeps working as before; we now sell a single ₦75K/mo subscription, so you're already on the highest tier we offer.
						</div>
					)}
				</div>
			</main>
		</div>
	);
};

export default PricingPage;
